import type { FormFieldMapping } from '../types/fieldMapping';
import type {
  FormFieldStackEntry,
  FormInlineEachEntry,
  FormLayoutNode,
  FormFieldsNode,
  FormTabsNode,
  FormConditionalNode,
} from '../types/layout';
import type { FormSchema } from '../types/schema';

function storeKeyFromField(field: FormFieldMapping): string | undefined {
  if (field.def?.adapterHint !== 'document-list') return undefined;
  const adapterProps =
    field.adapterProps ?? (field.def?.adapterProps as Record<string, unknown> | undefined);
  return typeof adapterProps?.storeKey === 'string' ? adapterProps.storeKey : undefined;
}

function collectFromFieldEntries(
  entries: Array<FormFieldMapping | { type: string }>,
  keys: Set<string>,
): void {
  for (const entry of entries) {
    if ('type' in entry) {
      const type = entry.type;
      if (type === 'stack') {
        collectFromFieldEntries((entry as FormFieldStackEntry).fields, keys);
      }
      continue;
    }
    const key = storeKeyFromField(entry as FormFieldMapping);
    if (key) keys.add(key);
  }
}

export function collectDocumentListStoreKeysFromLayout(layout: FormLayoutNode[]): string[] {
  const keys = new Set<string>();

  function walk(nodes: FormLayoutNode[]): void {
    for (const node of nodes) {
      switch (node.type) {
        case 'fields':
          collectFromFieldEntries((node as FormFieldsNode).fields, keys);
          break;
        case 'section':
        case 'grid':
        case 'stack':
        case 'surface':
          walk(node.children);
          break;
        case 'tabs':
          for (const tab of (node as FormTabsNode).tabs) walk(tab.children);
          break;
        case 'conditional': {
          const cond = node as FormConditionalNode;
          walk(cond.children);
          if (cond.else) walk(cond.else);
          break;
        }
        case 'column-group':
          for (const group of node.groups) {
            for (const entry of group.fields) {
              if ('type' in entry && entry.type === 'inline-each') continue;
              if ('type' in entry) continue;
              const key = storeKeyFromField(entry as FormFieldMapping);
              if (key) keys.add(key);
            }
          }
          break;
        default:
          break;
      }
    }
  }

  walk(layout);
  return [...keys];
}

/** Resolve document-list store registry keys from a loaded FormSchema layout tree. */
export function collectDocumentListStoreKeys(schema: FormSchema, layout: FormLayoutNode[]): string[] {
  void schema;
  return collectDocumentListStoreKeysFromLayout(layout);
}
